import React, { useState } from 'react';
import { Card, Input, Form, Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import PaginatedTable, { PaginatedTableProps } from '../../../../common/PaginatedTable';
import { UserSearchResult } from '../types/user-search.types';
import { UserSearchFilters } from '../types/user-search.types';
import { useTranslation } from 'react-i18next';

interface UserSearchComponentProps
  extends Omit<PaginatedTableProps<UserSearchResult>, 'onFilterChange'> {
  filters: UserSearchFilters;
  onSearch: (filters: UserSearchFilters) => void;
}

const UserSearchComponent = (props: UserSearchComponentProps) => {
  const { t } = useTranslation();
  const { filters, onSearch } = props;

  // Local state for filter inputs (not applied until search is clicked)
  const [localFilters, setLocalFilters] = useState<UserSearchFilters>(filters);

  const handleFilterChange = (field: string, value: string) => {
    setLocalFilters({
      ...localFilters,
      [field]: value || undefined,
    });
  };

  const handleSearch = () => {
    // Clean up filters - remove empty strings and undefined values
    const cleanedFilters: UserSearchFilters = {};
    Object.entries(localFilters).forEach(([key, value]) => {
      if (value && value.trim()) {
        cleanedFilters[key as keyof UserSearchFilters] = value.trim();
      }
    });
    onSearch(cleanedFilters);
  };

  // Update local filters when props.filters change (e.g., from URL)
  React.useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  return (
    <>
      <Card className="shadow-lg mb-3">
        <Form layout="vertical">
          <div className="row g-3">
            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
              <Form.Item label={t('userSearch.employeeNumber.label')}>
                <Input
                  placeholder={t('userSearch.employeeNumber.placeholder')}
                  value={localFilters.employeeNumber || ''}
                  onChange={(e) => handleFilterChange('employeeNumber', e.target.value)}
                  allowClear
                />
              </Form.Item>
            </div>
            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
              <Form.Item label={t('userSearch.passportNumber.label')}>
                <Input
                  placeholder={t('userSearch.passportNumber.placeholder')}
                  value={localFilters.passportNumber || ''}
                  onChange={(e) => handleFilterChange('passportNumber', e.target.value)}
                  allowClear
                />
              </Form.Item>
            </div>
            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
              <Form.Item label={t('userSearch.applicationNumber.label')}>
                <Input
                  placeholder={t('userSearch.applicationNumber.placeholder')}
                  value={localFilters.applicationNumber || ''}
                  onChange={(e) => handleFilterChange('applicationNumber', e.target.value)}
                  allowClear
                />
              </Form.Item>
            </div>
            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
              <Form.Item label={t('userSearch.visaNumber.label')}>
                <Input
                  placeholder={t('userSearch.visaNumber.placeholder')}
                  value={localFilters.visaNumber || ''}
                  onChange={(e) => handleFilterChange('visaNumber', e.target.value)}
                  allowClear
                />
              </Form.Item>
            </div>
            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
              <Form.Item label={t('userSearch.uidNumber.label')}>
                <Input
                  placeholder={t('userSearch.uidNumber.placeholder')}
                  value={localFilters.uidNumber || ''}
                  onChange={(e) => handleFilterChange('uidNumber', e.target.value)}
                  allowClear
                />
              </Form.Item>
            </div>
            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
              <Form.Item label={t('userSearch.eidNumber.label')}>
                <Input
                  placeholder={t('userSearch.eidNumber.placeholder')}
                  value={localFilters.eidNumber || ''}
                  onChange={(e) => handleFilterChange('eidNumber', e.target.value)}
                  allowClear
                />
              </Form.Item>
            </div>
          </div>
          <div className="row">
            <div className="col-12 text-end mt-3">
              <Button
                type="primary"
                htmlType="button"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                size="middle"
                style={{ height: 36 }}
              >
                {t('userSearch.search.label')}
              </Button>
            </div>
          </div>
        </Form>
      </Card>
      <Card className="shadow-lg">
        <PaginatedTable<UserSearchResult>
          data={props.data}
          columns={props.columns}
          loading={props.loading}
          pagination={props.pagination}
          rowKey={props.rowKey}
          onPageChange={props.onPageChange}
          onPageSizeChange={props.onPageSizeChange}
          className={props.className}
          showSizeChanger={props.showSizeChanger}
          tableProps={props.tableProps}
          showSearchBar={props.showSearchBar}
          onSearchChange={props.onSearchChange}
          showFilters={props.showFilters}
          availableFilters={props.availableFilters}
          onFilterChange={undefined}
          sortField={props.sortField}
          sortOrder={props.sortOrder}
        />
      </Card>
    </>
  );
};

export default React.memo(UserSearchComponent);
